import { useCallback, useEffect, useState, useMemo } from 'react'
import { PscPhrase } from '../components/psc/psc-phrase'

export const ENTITY_TYPES = {
    QMS_DOCUMENT: {
        name: 'QMS Document',
        id: 'qms_documents',
        kind: 'Document',
        fields: {
            type: 'qms_document_type',
            typeId: 'qms_document_type_id',
            areaId: 'qms_document_area_id',
            statusId: 'qms_document_status_id',
            date: 'issue_date',
        }
    },
    QA_DOCUMENT: {
        name: 'QA Document',
        id: 'qa_documents',
        kind: 'Document',
        fields: {
            type: 'qa_document_type',
            typeId: 'qa_document_type_id',
            areaId: 'qa_document_area_id',
            statusId: 'qa_document_status_id',
            date: 'issue_date',
        }
    },
    QA_RECORD: {
        name: 'QA Record',
        id: 'qa_records',
        kind: 'Record',
        fields: {
            type: 'qa_record_type',
            typeId: 'qa_record_type_id',
            statusId: 'qa_record_status_id',
            date: 'create_date',
        }
    }
}

const useQualityEntity = ({
    entityType,
    id,
    areaOptions,
    typeOptions,
    statusOptions,
    isNew,
}) => {
    const [data, setData] = useState(null)
    
    const [isLoading, setIsLoading] = useState(!isNew)
    const [noAccess, setNoAccess] = useState(false)

    const labels = useMemo(() => {
        const defaultLabels = {
            titleLabel: PscPhrase('LABEL_TITLE'),
            referenceLabel: PscPhrase('LABEL_REFERENCE'),
            classificationLabel: PscPhrase('LABEL_CLASSIFICATION'),
            filesLabel: PscPhrase('LABEL_FILE_LOCATION'),
            autoCreatedInfoText: PscPhrase('LABEL_CREATED_AUTOMATICALLY'),
        }
        switch (entityType.id) {
            case ENTITY_TYPES.QMS_DOCUMENT.id:
                return {
                    ...defaultLabels,
                    areaLabel: PscPhrase('QMS_DOCUMENT_LABEL_AREA'),
                    typeLabel: PscPhrase('QMS_DOCUMENT_LABEL_TYPE'),
                    noteLabel: PscPhrase('QMS_DOCUMENT_LABEL_NOTE'),
                    ownerLabel: PscPhrase('QMS_DOCUMENT_LABEL_OWNER'),
                    dateLabel: PscPhrase('QMS_DOCUMENT_LABEL_ISSUE_DATE'),
                    isoSectionLabel: PscPhrase('QMS_DOCUMENT_LABEL_ISO_SECTION_NUMBER'),
                    noteInfoText: PscPhrase('QMS_DOCUMENT_INFO_NOTE'),
                }

            case ENTITY_TYPES.QA_DOCUMENT.id:
                return {
                    ...defaultLabels,
                    areaLabel: PscPhrase('QA_DOCUMENT_LABEL_AREA'),
                    typeLabel: PscPhrase('QA_DOCUMENT_LABEL_TYPE'),
                    noteLabel: PscPhrase('QA_DOCUMENT_LABEL_NOTE'),
                    ownerLabel: PscPhrase('QA_DOCUMENT_LABEL_OWNER'),
                    dateLabel: PscPhrase('QA_DOCUMENT_LABEL_ISSUE_DATE'),
                    isoSectionLabel: PscPhrase('QA_DOCUMENT_LABEL_ISO_SECTION_NUMBER'),
                    noteInfoText: PscPhrase('QA_DOCUMENT_INFO_NOTE'),
                }

            case ENTITY_TYPES.QA_RECORD.id:
                return {
                    ...defaultLabels,
                    typeLabel: PscPhrase('QA_RECORD_LABEL_TYPE'),
                    noteLabel: PscPhrase('QA_RECORD_LABEL_NOTE'),
                    ownerLabel: PscPhrase('QA_RECORD_LABEL_OWNER'),
                    dateLabel: PscPhrase('QA_RECORD_LABEL_CREATE_DATE'),
                }

            default:
                return {}
        }
    }, [entityType])

    const roles = useMemo(() => {
        switch (entityType) {
            case ENTITY_TYPES.QMS_DOCUMENT:
                return {
                    adminRole: 'qms_documents_admin',
                    userRole: 'qms_documents_user',
                    readOnlyRole: 'qms_documents_readonly',
                }

            case ENTITY_TYPES.QA_DOCUMENT:
                return {
                    adminRole: 'qa_documents_admin',
                    userRole: 'qa_documents_user',
                    readOnlyRole: 'qa_documents_readonly',
                }

            case ENTITY_TYPES.QA_RECORD:
                return {
                    adminRole: 'qa_records_admin',
                    userRole: 'qa_records_user',
                    readOnlyRole: 'qa_records_readonly',
                }

            default:
                return {}
        }
    }, [entityType])

    const getByEntityType = useCallback((data) => {
        const { characteristics } = data || {}
        switch (entityType) {
            case ENTITY_TYPES.QMS_DOCUMENT:
                return {
                    areaId: data?.qms_document_area_id,
                    typeId: data?.qms_document_type_id,
                    statusId: data?.qms_document_status_id,
                    date: data?.issue_date,
                    characteristics: {
                        hasLaterNotWithdrawn: characteristics?.has_later_not_withdrawn,
                        hasLaterLiveOrSuperseded: characteristics?.has_later_live_or_superseded,
                    }
                }

            case ENTITY_TYPES.QA_DOCUMENT:
                return {
                    areaId: data?.qa_document_area_id,
                    typeId: data?.qa_document_type_id,
                    statusId: data?.qa_document_status_id,
                    date: data?.issue_date,
                    characteristics: {
                        hasLaterNotWithdrawn: characteristics?.has_later_not_withdrawn,
                        hasLaterLiveOrSuperseded: characteristics?.has_later_live_or_superseded,
                    }
                }

            case ENTITY_TYPES.QA_RECORD:
                return {
                    typeId: data?.qa_record_type_id,
                    statusId: data?.qa_record_status_id,
                    date: data?.create_date,
                    characteristics: {
                        hasLaterNotWithdrawn: characteristics?.has_later_not_withdrawn,
                        hasLaterLiveOrSuperseded: characteristics?.has_later_live_or_superseded,
                    }
                }

            default:
                return {}
        }
    }, [data])

    const fetchData = useCallback(async () => {
        const response = await fetch(`/v0/${entityType.id}/${id}`, { credentials: 'same-origin' })

        if (!response.ok) {
            throw new Error(response.statusText)
        }

        const json = await response.json()

        if (json.error || json.result.id === 0) {
            throw new Error(json.error)
        }

        const { result } = json

        const {
            areaId,
            typeId,
            statusId,
            date,
            characteristics,
        } = getByEntityType(result)

        const area = areaOptions?.find((element) => element.id === areaId)
        const type = typeOptions.find((element) => element.id === typeId)
        const status = statusOptions.find((element) => element.id === statusId)

        setData({
            area,
            type,
            isoSectionNumber: result.iso_section_number,
            reference: result.reference,
            referenceWithoutVersion: result.reference_without_version,
            issue: result.issue,
            issueNumber: result.issue_number,
            revision: result.revision,
            title: result.title,
            classification: result.classification,
            note: result.note,
            ownerId: result.owner_user_id,
            date,
            status,
            filePaths: result.file_paths,
            finalVersionFilePath: result.final_version_file_path,
            characteristics,
            isUsed: result.is_used,
            updatedAt: result.updated_at,
        })
    }, [id, areaOptions, typeOptions, statusOptions])

    useEffect(() => {
        if (!isNew) {
            fetchData().catch(error => {
                console.log('useQualityEntity fetch error: ',error.message)
                setNoAccess(true);
            }).finally(() => setIsLoading(false))
        }
    }, [fetchData])

    return {
        entityFormData: data,
        labels,
        roles,
        isLoading,
        noAccess,
    }
}

export default useQualityEntity
